import { useCallback, useEffect, useState } from 'react';

import { useNavigation, useRouter } from 'expo-router';
import { BackHandler } from 'react-native';

import { useEnableOrderPendingProcessModalExperimentOrUnresolved } from 'experiments/use-order-pending-process-modal-experiment';
import {
  clearPendingSubmittedOrderActiveSubmission,
  getPendingSubmittedOrder,
  useShouldShowOrderRecoveryLoading,
} from 'utils/order/pending-submitted-order';

import {
  bypassCheckoutBackGuardOnce,
  consumeCheckoutBackGuardBypass,
} from './checkout-navigation-bypass';
import { useCheckoutContext } from './use-checkout-context';

const isOrderSubmissionInProgress = ({
  isBuildingCommitOrderPayload,
  isSubmittingOrder,
  wasCommitOrderCalled,
  includePendingSubmittedOrder,
}: {
  isBuildingCommitOrderPayload: boolean;
  isSubmittingOrder: boolean;
  wasCommitOrderCalled: boolean;
  includePendingSubmittedOrder: boolean;
}) => {
  if (isBuildingCommitOrderPayload || isSubmittingOrder || wasCommitOrderCalled) {
    return true;
  }

  if (!includePendingSubmittedOrder) {
    return false;
  }

  return Boolean(getPendingSubmittedOrder()?.rbiOrderId);
};

export const useCheckoutBackNavigationGuard = () => {
  const router = useRouter();
  const navigation = useNavigation();
  const isOrderPendingProcessModalEnabled = useEnableOrderPendingProcessModalExperimentOrUnresolved();
  const {
    isBuildingCommitOrderPayload,
    isPaymentRedirectPending,
    isSubmittingOrder,
    wasCommitOrderCalled,
  } = useCheckoutContext();
  const [forcedRecoveryLoading, setForcedRecoveryLoading] = useState(false);
  const shouldShowRecoveryLoadingFromStorage = useShouldShowOrderRecoveryLoading();
  const pendingSubmittedOrder = isOrderPendingProcessModalEnabled
    ? getPendingSubmittedOrder()
    : null;
  const hasPendingSubmittedOrder = Boolean(pendingSubmittedOrder?.rbiOrderId);
  const showRecoveryLoading =
    isOrderPendingProcessModalEnabled &&
    (forcedRecoveryLoading || shouldShowRecoveryLoadingFromStorage);

  const orderSubmissionInProgress =
    isPaymentRedirectPending ||
    (isOrderPendingProcessModalEnabled &&
      isOrderSubmissionInProgress({
        isBuildingCommitOrderPayload,
        isSubmittingOrder,
        wasCommitOrderCalled,
        includePendingSubmittedOrder: true,
      }));

  const triggerRecoveryLoading = useCallback(() => {
    if (!isOrderPendingProcessModalEnabled) {
      return;
    }

    const pendingSubmittedOrder = getPendingSubmittedOrder();

    if (pendingSubmittedOrder?.rbiOrderId) {
      clearPendingSubmittedOrderActiveSubmission(pendingSubmittedOrder.rbiOrderId);
    }

    setForcedRecoveryLoading(true);
  }, [isOrderPendingProcessModalEnabled]);

  useEffect(() => {
    if (
      !isOrderPendingProcessModalEnabled ||
      !orderSubmissionInProgress ||
      !hasPendingSubmittedOrder
    ) {
      setForcedRecoveryLoading(false);
    }
  }, [hasPendingSubmittedOrder, isOrderPendingProcessModalEnabled, orderSubmissionInProgress]);

  useEffect(() => {
    if (!isOrderPendingProcessModalEnabled) {
      return undefined;
    }

    return () => {
      const pendingSubmittedOrder = getPendingSubmittedOrder();

      if (pendingSubmittedOrder?.rbiOrderId) {
        clearPendingSubmittedOrderActiveSubmission(pendingSubmittedOrder.rbiOrderId);
      }
    };
  }, [isOrderPendingProcessModalEnabled]);

  useEffect(() => {
    if (!isOrderPendingProcessModalEnabled && !isPaymentRedirectPending) {
      return undefined;
    }

    const unsubscribe = navigation.addListener('beforeRemove', event => {
      if (consumeCheckoutBackGuardBypass()) {
        return;
      }

      if (orderSubmissionInProgress) {
        if (!isPaymentRedirectPending) {
          triggerRecoveryLoading();
        }
        event.preventDefault();
      }
    });

    return unsubscribe;
  }, [
    isOrderPendingProcessModalEnabled,
    isPaymentRedirectPending,
    navigation,
    orderSubmissionInProgress,
    triggerRecoveryLoading,
  ]);

  useEffect(() => {
    if (!isOrderPendingProcessModalEnabled && !isPaymentRedirectPending) {
      return undefined;
    }

    const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
      if (orderSubmissionInProgress) {
        if (!isPaymentRedirectPending) {
          triggerRecoveryLoading();
        }
        return true;
      }

      return false;
    });

    return () => subscription.remove();
  }, [
    isOrderPendingProcessModalEnabled,
    isPaymentRedirectPending,
    orderSubmissionInProgress,
    triggerRecoveryLoading,
  ]);

  const handleBackPress = useCallback(() => {
    if (orderSubmissionInProgress) {
      if (!isPaymentRedirectPending) {
        triggerRecoveryLoading();
      }
      return;
    }

    bypassCheckoutBackGuardOnce();
    router.dismiss();
  }, [
    isOrderPendingProcessModalEnabled,
    isPaymentRedirectPending,
    orderSubmissionInProgress,
    router,
    triggerRecoveryLoading,
  ]);

  return { handleBackPress, showRecoveryLoading };
};
